import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { computeReadiness } from "@/lib/readiness-tech-review";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, FlagBanner, Warning, CheckCircle, FloppyDisk, CheckSquareOffset, FileArrowUp, Scales, ClockCounterClockwise, DownloadSimple, Link as LinkIcon, Paperclip } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const STATUS_OPTIONS = ["Not Reviewed", "Acceptable", "Needs Clarification", "Concern", "Not Applicable"];
const PARTY_OPTIONS = ["Brand", "Co-Manufacturer", "Shared", "Third Party", "Not Applicable"];
const NOTIFICATION_OPTIONS = ["No Notification Required", "Notification Required", "Prior Written Approval Required"];
const CAUSE_OPTIONS = ["Brand", "Co-Man", "Supplier", "Undetermined"];
const OVERALL_STATUS_OPTIONS = ["Not Reviewed", "In Review", "Needs Clarification", "Concerns Identified", "Review Complete"];
const RESPONSIBLE_PARTY_OPTIONS = [
  "Brand / Client",
  "Co-Manufacturer",
  "Quality Assurance",
  "Regulatory",
  "Food Safety / HACCP",
  "R&D / Product Development",
  "Operations / Production",
  "Procurement / Supply Chain",
  "Packaging",
  "Legal Counsel",
  "Third-Party Lab / Consultant",
  "Shared Responsibility",
  "Not Applicable",
  "Other"
];
const FOLLOW_UP_ACTION_OPTIONS = [
  "No Follow-up Required",
  "Request Clarification",
  "Request Updated Document / Specification",
  "Request Corrective Action",
  "Request Testing / Validation",
  "Assign Internal Review",
  "Schedule Follow-up Discussion",
  "Refer to Legal Counsel",
  "Obtain Regulatory / Food-Safety Review",
  "Confirm Responsibility in Writing",
  "Refer Contract Language for Legal Review / Negotiation",
  "Monitor at Next Production Run",
  "Escalate Concern",
  "Other"
];
const DISCLAIMER = "This tool supports technical and commercialization review and does not constitute legal advice, regulatory approval, food-safety approval, or approval of a co-manufacturing agreement. Legal provisions should be reviewed by qualified counsel, and food-safety/regulatory matters should be reviewed by appropriately qualified professionals as applicable.";

const ATTACHMENT_CATEGORIES = [
  "Manufacturing Agreement/MSA",
  "Quality Agreement",
  "Product specification",
  "Formula/version",
  "Process instructions",
  "Ingredient specifications",
  "Packaging specifications",
  "COAs",
  "Certifications",
  "Audit documents",
  "Process authority documentation",
  "Shelf-life/testing reports"
];

function Badge({ children, variant = "default" }) {
  const colors = {
    default: "bg-stone-100 text-stone-700",
    success: "bg-emerald-100 text-emerald-800",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${colors[variant] || colors.default}`}>
      {children}
    </span>
  );
}

export default function TechnicalReviewWorkspace() {
  const { id } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("editor");

  // Local state for responses to allow editing
  const [responses, setResponses] = useState({});
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");
  const [externalExportItems, setExternalExportItems] = useState(new Set());
  const [overallStatus, setOverallStatus] = useState("Not Reviewed");
  const [draftRevision, setDraftRevision] = useState(1);
  
  // Attachments form
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentCategory, setAttachmentCategory] = useState(ATTACHMENT_CATEGORIES[0]);
  const [attachmentDesc, setAttachmentDesc] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  useEffect(() => {
    loadReview();
  }, [id]);

  const loadReview = async () => {
    try {
      const { data } = await api.get(`/admin/coman-technical-reviews/${id}`);
      setReview(data);
      setOverallStatus(data.overall_status || "Not Reviewed");
      setDraftRevision(data.latest?.draft_revision || 1);
      if (data?.latest?.sections) {
        const initial = {};
        data.latest.sections.forEach(sec => {
          sec.items?.forEach(item => {
            initial[item.id] = {
              status: item.status || "Not Reviewed",
              notes: item.notes || "",
              responsible_party: item.responsible_party || "",
              follow_up_action: item.follow_up_action || "",
              due_date: item.due_date || "",
              document_reference: item.document_reference || "",
              notification_requirement: item.notification_requirement || "",
              responsibility: item.responsibility || "",
              cause_classification: item.cause_classification || "",
            };
          });
        });
        setResponses(initial);
      }
    } catch (err) {
      toast.error("Failed to load review workspace");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (itemId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }));
  };

  const saveDraft = async (showToast = true, manageSaving = true) => {
    if (!review?.latest) return false;
    if (manageSaving) setSaving(true);
    try {
      const itemsById = new Map(
        (review.latest.sections || []).flatMap(section => section.items || []).map(item => [item.id, item])
      );
      const respArray = Object.keys(responses).map(itemId => {
        const value = responses[itemId];
        const item = itemsById.get(itemId) || {};
        const payload = {
          id: itemId,
          status: value.status || "Not Reviewed",
          notes: value.notes || "",
          responsible_party: value.responsible_party || "",
          follow_up_action: value.follow_up_action || "",
          due_date: value.due_date || null,
          document_reference: value.document_reference || "",
        };
        if (item.notification_requirement !== undefined) {
          payload.notification_requirement = value.notification_requirement || "No Notification Required";
        }
        if (item.responsibility !== undefined) {
          payload.responsibility = value.responsibility || "Not Applicable";
        }
        if (item.cause_classification !== undefined) {
          payload.cause_classification = value.cause_classification || "Undetermined";
        }
        return payload;
      });
      
      const { data } = await api.put(`/admin/coman-technical-reviews/${id}/draft`, {
        expected_revision: draftRevision,
        overall_status: overallStatus,
        responses: respArray
      });
      if (showToast) toast.success("Draft saved");
      setReview(data);
      setDraftRevision(data.latest?.draft_revision || draftRevision + 1);
      return data.latest?.draft_revision || draftRevision + 1;
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("This draft changed in another session. Reload before saving.");
      } else {
        toast.error("Failed to save draft");
      }
      console.error(err);
      return false;
    } finally {
      if (manageSaving) setSaving(false);
    }
  };

  const createNextVersion = async () => {
    if (!window.confirm("Create a new version snapshot? This will freeze the current version and start a new round.")) return;
    setSaving(true);
    try {
      const saved = await saveDraft(false, false);
      if (!saved) return;
      await api.post(`/admin/coman-technical-reviews/${id}/versions`, {
        expected_version_id: review.latest.id,
        overall_status: overallStatus
      });
      toast.success("New version created");
      await loadReview();
    } catch (err) {
      toast.error("Failed to create next version");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    if (!attachmentFile) return;
    setUploadingAttachment(true);
    try {
      const savedRevision = await saveDraft(false, false);
      if (!savedRevision) return;
      const formData = new FormData();
      formData.append("file", attachmentFile);
      await api.post(`/admin/coman-technical-reviews/${id}/draft/attachments?category=${encodeURIComponent(attachmentCategory)}&description=${encodeURIComponent(attachmentDesc)}&expected_revision=${savedRevision}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Attachment uploaded");
      setAttachmentFile(null);
      setAttachmentDesc("");
      document.getElementById("attachment-file-input").value = "";
      await loadReview();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("This draft changed in another session. Reload before uploading.");
      } else {
        toast.error("Failed to upload attachment");
      }
      console.error(err);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const fetchCompare = async () => {
    if (!compareFrom || !compareTo) {
      toast.error("Select both versions to compare");
      return;
    }
    setComparing(true);
    try {
      const { data } = await api.get(`/admin/coman-technical-reviews/${id}/compare?from_version=${compareFrom}&to_version=${compareTo}`);
      setComparison(data);
    } catch (err) {
      toast.error("Failed to compare versions");
      console.error(err);
    } finally {
      setComparing(false);
    }
  };

  const handleExportExternal = () => {
    if (externalExportItems.size === 0) {
      toast.error("Select at least one item to export");
      return;
    }
    const query = Array.from(externalExportItems).map(itemId => `item_ids=${encodeURIComponent(itemId)}`).join('&');
    openProtectedDocument(`/admin/coman-technical-reviews/${id}/versions/${review.latest.id}/export/external?format=html&${query}`);
  };

  const openProtectedDocument = async (path, filename = null) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (filename) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Unable to open the protected document");
      console.error(err);
    }
  };

  // Derived computations
  const sections = review?.latest?.sections || [];
  
  const redFlags = useMemo(() => {
    const flags = [];
    sections.forEach(sec => {
      sec.items?.forEach(item => {
        const status = responses[item.id]?.status;
        if (status === "Concern" || status === "Needs Clarification") {
          flags.push({ section: sec, item, status, notes: responses[item.id]?.notes });
        }
      });
    });
    return flags;
  }, [sections, responses]);

  const readiness = useMemo(() => {
    return computeReadiness(sections, responses);
  }, [sections, responses]);

  const toggleExternalExport = (itemId) => {
    setExternalExportItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const scrollToItem = (itemId) => {
    const el = document.getElementById(`item-${itemId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add("ring-2", "ring-emerald-400", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400", "ring-offset-2"), 2000);
    }
  };

  if (loading) return <Layout><div className="max-w-7xl mx-auto px-4 py-16 text-stone-500">Loading workspace...</div></Layout>;
  if (!review) return <Layout><div className="max-w-7xl mx-auto px-4 py-16 text-red-500">Review not found.</div></Layout>;

  return (
    <Layout>
      <div className="bg-stone-100 border-b border-stone-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin/technical-reviews" className="text-xs font-semibold text-stone-500 hover:text-stone-800 flex items-center gap-1 mb-4 w-fit">
            <ArrowLeft size={12} weight="bold" /> Back to Queue
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-1 min-w-0 max-w-full">
                <h1 className="min-w-0 max-w-full break-words text-2xl sm:text-3xl font-bold tracking-tight text-stone-900 leading-none">
                  {review.client_name}
                </h1>
                <Badge variant={overallStatus === "Review Complete" ? "success" : overallStatus.includes("Concern") || overallStatus === "Needs Clarification" ? "warning" : "default"}>
                  {overallStatus}
                </Badge>
                <Badge variant="default">v{review.latest?.version_number || 1}</Badge>
              </div>
              <div className="text-sm font-mono text-stone-500 mb-3">{review.product_sku}</div>
              
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-600">
                <div><span className="font-semibold text-stone-800">Co-Man:</span> {review.comanufacturer || 'N/A'}</div>
                <div><span className="font-semibold text-stone-800">Facility:</span> {review.facility || 'N/A'}</div>
                <div><span className="font-semibold text-stone-800">Date:</span> {new Date(review.review_date).toLocaleDateString()}</div>
              </div>
            </div>
            
            <div className="flex flex-col items-stretch sm:items-end gap-2 w-full md:w-auto min-w-0">
              <label className="text-[10px] font-bold text-stone-500 uppercase tracking-wider">
                Overall status
                <select
                  value={overallStatus}
                  onChange={(event) => setOverallStatus(event.target.value)}
                  className="mt-1 block w-full sm:w-56 h-9 rounded-md border-stone-300 bg-white text-sm normal-case tracking-normal text-stone-800"
                >
                  {OVERALL_STATUS_OPTIONS.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving}>
                  <FloppyDisk className="mr-1.5" size={16} /> Save Draft
                </Button>
                <Button size="sm" onClick={createNextVersion} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900 text-white">
                  <CheckSquareOffset className="mr-1.5" size={16} /> Mark Round Complete
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-8 items-start overflow-x-hidden">
        
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-6 min-w-0">
          <div className="flex border-b border-stone-200 overflow-x-auto max-w-full">
            <button 
              onClick={() => setActiveTab("editor")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "editor" ? "border-emerald-800 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              Review Editor
            </button>
            <button 
              onClick={() => setActiveTab("attachments")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "attachments" ? "border-emerald-800 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              Attachments
            </button>
            <button 
              onClick={() => setActiveTab("outputs")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "outputs" ? "border-emerald-800 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              Outputs & Reports
            </button>
            <button 
              onClick={() => setActiveTab("history")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "history" ? "border-emerald-800 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              History
            </button>
          </div>

          {activeTab === "editor" && (
            <div className="space-y-8">
              {sections.map((sec) => (
                <div key={sec.id} className="scroll-mt-24" id={`sec-${sec.id}`}>
                  <div className={`mb-4 pb-2 border-b-2 ${sec.id === "legal_review_required" ? "border-amber-200" : "border-emerald-800"}`}>
                    <h2 className="text-xl font-bold tracking-tight text-stone-900">{sec.title}</h2>
                    {sec.id === "legal_review_required" && (
                      <div className="mt-1 text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-1 rounded inline-flex items-center gap-1.5">
                        <Scales size={14} weight="fill" />
                        Qualified counsel review only — no recommendations
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    {sec.items?.map(item => (
                      <Card key={item.id} id={`item-${item.id}`} className={`p-4 border-l-4 transition-all duration-300
                        ${responses[item.id]?.status === 'Concern' ? 'border-l-red-500 bg-red-50/30' : 
                          responses[item.id]?.status === 'Needs Clarification' ? 'border-l-amber-500 bg-amber-50/30' : 
                          responses[item.id]?.status === 'Acceptable' ? 'border-l-emerald-500' : 'border-l-stone-300'}`}>
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="flex-1">
                            <div className="font-semibold text-stone-900 text-sm mb-2">{item.label}</div>
                            <Textarea 
                              placeholder="Review notes (internal & external)..." 
                              className="text-sm min-h-[80px]"
                              value={responses[item.id]?.notes || ""}
                              onChange={(e) => handleResponseChange(item.id, 'notes', e.target.value)}
                            />
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 min-w-0">
                              {(item.responsibility !== undefined || item.type === "responsibility") && (
                                <div>
                                  <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Responsible Matrix</label>
                                  <select 
                                    className="w-full text-sm border-stone-200 rounded-md focus:ring-emerald-700/40 focus:border-emerald-700 h-9"
                                    value={responses[item.id]?.responsibility || ""}
                                    onChange={(e) => handleResponseChange(item.id, 'responsibility', e.target.value)}
                                  >
                                    <option value="" disabled>Select responsibility...</option>
                                    {PARTY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                </div>
                              )}
                              {(item.notification_requirement !== undefined || item.type === "change_control") && (
                                <div>
                                  <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Notification Req</label>
                                  <select 
                                    className="w-full text-sm border-stone-200 rounded-md focus:ring-emerald-700/40 focus:border-emerald-700 h-9"
                                    value={responses[item.id]?.notification_requirement || ""}
                                    onChange={(e) => handleResponseChange(item.id, 'notification_requirement', e.target.value)}
                                  >
                                    <option value="" disabled>Select requirement...</option>
                                    {NOTIFICATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                </div>
                              )}
                              {(item.cause_classification !== undefined || item.type === "cause_classification") && (
                                <div className="col-span-2">
                                  <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Cause Classification</label>
                                  <select
                                    className="w-full text-sm border-stone-200 rounded-md focus:ring-emerald-700/40 focus:border-emerald-700 h-9"
                                    value={responses[item.id]?.cause_classification || ""}
                                    onChange={(e) => handleResponseChange(item.id, 'cause_classification', e.target.value)}
                                  >
                                    {CAUSE_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                                  </select>
                                </div>
                              )}
                              
                              <div>
                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Responsible Party</label>
                                <select
                                  className="w-full h-9 text-sm border-stone-200 rounded-md focus:ring-emerald-700/40 focus:border-emerald-700"
                                  value={responses[item.id]?.responsible_party || ""}
                                  onChange={(e) => handleResponseChange(item.id, 'responsible_party', e.target.value)}
                                >
                                  <option value="">Select responsible party...</option>
                                  {responses[item.id]?.responsible_party &&
                                    !RESPONSIBLE_PARTY_OPTIONS.includes(responses[item.id].responsible_party) && (
                                      <option value={responses[item.id].responsible_party}>
                                        {responses[item.id].responsible_party}
                                      </option>
                                    )}
                                  {RESPONSIBLE_PARTY_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Follow-up Action</label>
                                <select
                                  className="w-full h-9 text-sm border-stone-200 rounded-md focus:ring-emerald-700/40 focus:border-emerald-700"
                                  value={responses[item.id]?.follow_up_action || ""}
                                  onChange={(e) => handleResponseChange(item.id, 'follow_up_action', e.target.value)}
                                >
                                  <option value="">Select follow-up action...</option>
                                  {responses[item.id]?.follow_up_action &&
                                    !FOLLOW_UP_ACTION_OPTIONS.includes(responses[item.id].follow_up_action) && (
                                      <option value={responses[item.id].follow_up_action}>
                                        {responses[item.id].follow_up_action}
                                      </option>
                                    )}
                                  {FOLLOW_UP_ACTION_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Due Date</label>
                                <Input 
                                  type="date" 
                                  className="h-9 text-sm" 
                                  value={responses[item.id]?.due_date || ""}
                                  onChange={(e) => handleResponseChange(item.id, 'due_date', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Doc / Reference</label>
                                <Input 
                                  className="h-9 text-sm" 
                                  placeholder="Filename or link..."
                                  value={responses[item.id]?.document_reference || ""}
                                  onChange={(e) => handleResponseChange(item.id, 'document_reference', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="w-full md:w-48 shrink-0">
                            <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">Status</label>
                            <select 
                              className={`w-full text-sm font-semibold rounded-md h-10 border-stone-200 focus:ring-emerald-700/40 focus:border-emerald-700
                                ${responses[item.id]?.status === 'Concern' ? 'bg-red-50 text-red-900 border-red-300' : 
                                  responses[item.id]?.status === 'Needs Clarification' ? 'bg-amber-50 text-amber-900 border-amber-300' : 
                                  responses[item.id]?.status === 'Acceptable' ? 'bg-emerald-50 text-emerald-900 border-emerald-300' : 
                                  'bg-white text-stone-700'}`}
                              value={responses[item.id]?.status || "Not Reviewed"}
                              onChange={(e) => handleResponseChange(item.id, 'status', e.target.value)}
                            >
                              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "attachments" && (
            <div className="space-y-6">
              <Card className="p-6 border-stone-200">
                <h3 className="text-lg font-bold text-stone-900 mb-4">Upload Attachment</h3>
                <form onSubmit={handleUploadAttachment} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-1">Category</label>
                    <select 
                      className="w-full text-sm border-stone-200 rounded-md focus:ring-emerald-700/40"
                      value={attachmentCategory}
                      onChange={(e) => setAttachmentCategory(e.target.value)}
                    >
                      {ATTACHMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-1">Description (Optional)</label>
                    <Input 
                      className="text-sm" 
                      placeholder="Context or item reference..."
                      value={attachmentDesc}
                      onChange={(e) => setAttachmentDesc(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-1">File</label>
                    <input 
                      id="attachment-file-input"
                      type="file" 
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-stone-50 file:text-stone-700 hover:file:bg-stone-100 cursor-pointer"
                      onChange={(e) => setAttachmentFile(e.target.files[0])}
                    />
                  </div>
                  <Button type="submit" disabled={!attachmentFile || uploadingAttachment} className="bg-stone-800 text-white hover:bg-stone-900">
                    {uploadingAttachment ? "Uploading..." : "Upload File"}
                  </Button>
                </form>
              </Card>

              {review.latest?.attachments?.length > 0 && (
                <Card className="p-6 border-stone-200">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Current Attachments</h3>
                  <div className="divide-y divide-stone-100">
                    {review.latest.attachments.map(att => (
                      <div key={att.id} className="py-3 flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-stone-900 flex items-center gap-1.5">
                            <Paperclip size={14} className="text-stone-400" />
                            {att.filename}
                          </div>
                          <div className="text-xs text-stone-500 mt-1">
                            <span className="font-medium text-stone-700">{att.category}</span>
                            {att.description && <span> • {att.description}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openProtectedDocument(
                            `/admin/coman-technical-reviews/${id}/versions/${review.latest.id}/attachments/${att.id}/download`,
                            att.filename
                          )}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 px-3 py-1.5 rounded-md hover:bg-emerald-50 transition-colors"
                        >
                          <DownloadSimple size={14} weight="bold" /> Download
                        </button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {activeTab === "outputs" && (
            <div className="space-y-6">
              <Card className="p-6 border-stone-200">
                <h3 className="text-lg font-bold text-stone-900 mb-2">Internal Review Summary</h3>
                <p className="text-sm text-stone-600 mb-4">Generates a complete readout of all sections, including legal reviews and internal notes.</p>
                <button
                  type="button"
                  onClick={() => openProtectedDocument(`/admin/coman-technical-reviews/${id}/versions/${review.latest.id}/export/internal?format=html`)}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-emerald-800 text-white hover:bg-emerald-900"
                >
                  <LinkIcon size={16} className="mr-2" /> Open Internal Report
                </button>
              </Card>

              <Card className="p-6 border-stone-200">
                <h3 className="text-lg font-bold text-stone-900 mb-2">External Client Report</h3>
                <p className="text-sm text-stone-600 mb-4">Export a sanitized report for the client. Requires explicit selection of items to include. Legal Review and internal-only notes are automatically excluded.</p>
                <div className="p-4 bg-stone-50 rounded border border-stone-200 text-sm text-stone-500 mb-4 flex items-start gap-2">
                  <Warning size={16} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
                  <div>By default, no items are selected for external export. Check items below to include them.</div>
                </div>
                
                <div className="border border-stone-200 rounded-md bg-white overflow-hidden mb-4">
                  <div className="max-h-64 overflow-y-auto p-4 space-y-3">
                    {sections.map(sec => {
                      if (sec.id === "legal_review_required") return null;
                      return (
                        <div key={`ext-sec-${sec.id}`}>
                          <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">{sec.title}</div>
                          {sec.items?.map(item => (
                            <label key={`ext-item-${item.id}`} className="flex items-start gap-2 text-sm text-stone-800 mb-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                className="mt-1 text-emerald-700 focus:ring-emerald-700/40 rounded border-stone-300"
                                checked={externalExportItems.has(item.id)}
                                onChange={() => toggleExternalExport(item.id)}
                              />
                              <span className="leading-tight">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Button 
                  onClick={handleExportExternal} 
                  disabled={externalExportItems.size === 0}
                  variant="outline"
                >
                  <DownloadSimple size={16} className="mr-2" /> Download Selected ({externalExportItems.size})
                </Button>
              </Card>
              
              <Card className="p-6 border-stone-200">
                <h3 className="text-lg font-bold text-stone-900 mb-2">Meeting Agenda</h3>
                <p className="text-sm text-stone-600 mb-4">Generates an agenda based purely on items flagged as Concern or Needs Clarification.</p>
                <Button variant="outline" onClick={() => {
                   const content = `Co-Man Meeting Agenda\n${review.client_name} - ${review.product_sku}\n\nUnresolved Questions:\n\n${redFlags.map(f => `- ${f.section.title}: ${f.item.label}\n  Status: ${f.status}\n  Notes: ${f.notes || "No notes entered."}`).join('\n\n')}\n\n${DISCLAIMER}`;
                  const blob = new Blob([content], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Agenda_${review.client_name.replace(/\s+/g, "_")}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}>
                  <DownloadSimple size={16} className="mr-2" /> Download Agenda
                </Button>
              </Card>
            </div>
          )}

          {activeTab === "history" && (
            <div className="space-y-6">
              <Card className="p-6 border-stone-200">
                <h3 className="text-lg font-bold text-stone-900 mb-4">Version Comparison</h3>
                <div className="flex flex-col sm:flex-row items-end gap-4 mb-6">
                  <div className="flex-1 w-full">
                    <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">From Version</label>
                    <select 
                      className="w-full text-sm rounded-md border-stone-200 h-9"
                      value={compareFrom}
                      onChange={(e) => setCompareFrom(e.target.value)}
                    >
                      <option value="" disabled>Select version...</option>
                      {(review.versions || []).map(v => <option key={`from-${v.id}`} value={v.id}>v{v.version_number}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 w-full">
                    <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">To Version</label>
                    <select 
                      className="w-full text-sm rounded-md border-stone-200 h-9"
                      value={compareTo}
                      onChange={(e) => setCompareTo(e.target.value)}
                    >
                      <option value="" disabled>Select version...</option>
                      {(review.versions || []).map(v => <option key={`to-${v.id}`} value={v.id}>v{v.version_number}</option>)}
                    </select>
                  </div>
                  <Button onClick={fetchCompare} disabled={comparing || !compareFrom || !compareTo} className="bg-emerald-800 text-white shrink-0 w-full sm:w-auto">
                    {comparing ? "Comparing..." : "Compare Changes"}
                  </Button>
                </div>
                
                {comparison ? (
                  <div className="border border-stone-200 rounded-md overflow-x-auto max-w-full">
                    <table className="min-w-full text-left text-sm divide-y divide-stone-200">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-4 py-2 font-semibold text-stone-500">Item</th>
                          <th className="px-4 py-2 font-semibold text-stone-500">Old Value</th>
                          <th className="px-4 py-2 font-semibold text-stone-500">New Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100 bg-white">
                        {comparison.changes?.length > 0 ? comparison.changes.map((c, i) => (
                          <tr key={i}>
                             <td className="px-4 py-3 align-top font-medium text-stone-900">{c.label || c.item_id}</td>
                             <td className="px-4 py-3 align-top text-red-700 bg-red-50/50">
                               <pre className="whitespace-pre-wrap break-words text-xs font-sans">{JSON.stringify(c.before, null, 2)}</pre>
                             </td>
                             <td className="px-4 py-3 align-top text-emerald-700 bg-emerald-50/50">
                               <pre className="whitespace-pre-wrap break-words text-xs font-sans">{JSON.stringify(c.after, null, 2)}</pre>
                             </td>
                          </tr>
                        )) : (
                          <tr><td colSpan={3} className="px-4 py-8 text-center text-stone-500">No changes between these versions.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-stone-500 border border-stone-100 bg-stone-50 rounded-md p-8 text-center">
                    <ClockCounterClockwise size={24} className="mx-auto text-stone-400 mb-2" />
                    Select two versions above to view highlighted differences in statuses, notes, and responsibilities.
                  </div>
                )}
              </Card>
              
              <h3 className="text-lg font-bold text-stone-900 mb-2 mt-8">Snapshot History</h3>
              {review.versions?.map(v => (
                <div key={v.id} className="flex items-center justify-between p-4 bg-white border border-stone-200 rounded-lg">
                  <div>
                    <div className="font-bold text-stone-900">Version {v.version_number}</div>
                    <div className="text-sm text-stone-500">Created {new Date(v.created_at || Date.now()).toLocaleString()}</div>
                  </div>
                   <Button variant="outline" size="sm" onClick={() => openProtectedDocument(`/admin/coman-technical-reviews/${id}/versions/${v.id}/export/internal?format=html`)}>
                    View Snapshot Report
                  </Button>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Right Sidebar: Intelligence & Navigation */}
        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 min-w-0">
          
          <Card className="p-5 border-stone-200 bg-stone-800 text-white">
            <h3 className="text-xs uppercase tracking-wider font-bold text-stone-400 mb-4 flex items-center gap-1.5">
              <CheckCircle size={14} weight="bold" /> Readiness
            </h3>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-4xl font-bold tracking-tight leading-none">{readiness.pct}%</span>
              <span className="text-sm text-stone-400 font-medium mb-1">resolved</span>
            </div>
            <div className="w-full bg-stone-700 h-2 rounded-full overflow-hidden mt-3 mb-4">
              <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${readiness.pct}%` }} />
            </div>
            <div className="text-[10px] text-stone-400 leading-tight border-t border-stone-700 pt-3">
              APPROVAL CAVEAT: Score excludes legal review. Unresolved critical items may block commercialization regardless of total score.
            </div>
            <div className="mt-4 space-y-2 border-t border-stone-700 pt-3">
              {readiness.sections.map(section => (
                <div key={section.id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="truncate text-stone-300">{section.title}</span>
                  <span className="font-semibold text-white">{section.pct}%</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 border-stone-200">
            <h3 className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-4 flex items-center gap-1.5">
              <FlagBanner size={14} weight="bold" className="text-amber-500" /> Red Flag Dashboard
            </h3>
            
            {redFlags.length === 0 ? (
              <div className="text-sm text-stone-500 text-center py-6">
                <CheckCircle size={24} className="mx-auto text-emerald-500 mb-2" />
                No red flags detected.
              </div>
            ) : (
              <div className="space-y-3">
                {redFlags.map((flag, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => scrollToItem(flag.item.id)}
                    className="w-full text-left bg-white border border-stone-200 rounded p-3 hover:border-emerald-500 transition-colors shadow-sm"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                        ${flag.status === 'Concern' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                        {flag.status}
                      </span>
                    </div>
                    <div className="text-xs font-bold text-stone-900 leading-tight mb-1">{flag.item.label}</div>
                    <div className="text-[10px] text-stone-500 truncate">{flag.section.title}</div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5 border-stone-200">
            <h3 className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-3">Quick Navigation</h3>
            <div className="space-y-1">
              {sections.map(sec => (
                <a 
                  key={sec.id} 
                  href={`#sec-${sec.id}`}
                  className="block text-sm text-stone-600 hover:text-emerald-700 hover:bg-stone-50 px-2 py-1.5 rounded transition-colors"
                >
                  {sec.title}
                </a>
              ))}
            </div>
          </Card>

          <Card className="p-5 border-amber-200 bg-amber-50 text-xs leading-relaxed text-amber-950">
            <div className="font-bold uppercase tracking-wider mb-2">Review limitation</div>
            {DISCLAIMER}
          </Card>
          
        </div>

      </div>
    </Layout>
  );
}
